import { useEffect, useMemo, useState, useActionState, startTransition } from 'react'
import { Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap'
import { IconPackage, IconSearch, IconX } from '@tabler/icons-react'
//@ts-ignore
import { Button, Checkbox, InputGroup } from 'dsm-react-lib'
import { useDataboxService } from '../../Teams/services/databoxService'
import { useThemeManager } from '../../../../_hooks/useThemeManager'
import { getFileIcon } from '../utils/helpers'

export interface SelectedDataboxFile {
  id: string
  name: string
  databoxId: string
  databoxName: string
}

interface AddDataboxFilesModalProps {
  isOpen: boolean
  toggle: () => void
  // Document ids already attached to the project, shown as disabled/added.
  existingFileIds?: string[]
  // Attach the chosen files to the project. May be async; the modal shows a
  // submitting state until it resolves, then closes.
  onConfirm: (files: SelectedDataboxFile[]) => void | Promise<void>
}

interface DataboxSummary {
  id: string
  name: string
  filesCount: number
}

// Resolve a stable document id from the various shapes the API may return.
const getDocId = (doc: any): string =>
  String(doc?.id ?? doc?.smartDocsDocumentId ?? doc?.externalId ?? '')

const AddDataboxFilesModal: React.FC<AddDataboxFilesModalProps> = ({
  isOpen,
  toggle,
  existingFileIds = [],
  onConfirm
}) => {
  const { isDarkTheme: isDarkMode } = useThemeManager()
  const { getMyDataboxes, getDataboxDocumentsPaginated } = useDataboxService()

  const [databox, setDatabox] = useState<DataboxSummary | null>(null)
  const [isLoadingDatabox, setIsLoadingDatabox] = useState(false)
  const [documents, setDocuments] = useState<any[]>([])
  const [isLoadingDocs, setIsLoadingDocs] = useState(false)
  const [docSearch, setDocSearch] = useState('')

  // Selected files for the personal databox, keyed by file id.
  const [selectedFiles, setSelectedFiles] = useState<Record<string, string>>({})

  const existingIdSet = useMemo(() => new Set(existingFileIds.map(String)), [existingFileIds])

  // Reset everything and load the personal databox + its files whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return
    setDocSearch('')
    setSelectedFiles({})
    setDocuments([])
    setDatabox(null)
    ;(async () => {
      setIsLoadingDatabox(true)
      const response = await getMyDataboxes()
      const items = response?.items ?? response ?? []
      const personalDatabox = (Array.isArray(items) ? items : []).find(
        (db: any) => String(db?.scope ?? '').toLowerCase() === 'personal'
      )
      setIsLoadingDatabox(false)
      if (!personalDatabox) return

      const summary: DataboxSummary = {
        id: String(personalDatabox.id),
        name: personalDatabox.name ?? 'Personal databox',
        filesCount: personalDatabox.filesCount ?? personalDatabox.databoxDocumentsCount ?? 0
      }
      setDatabox(summary)

      setIsLoadingDocs(true)
      const docsResponse = await getDataboxDocumentsPaginated(
        summary.id,
        1,
        100,
        undefined,
        'uploadTime',
        'desc'
      )
      setDocuments(Array.isArray(docsResponse) ? docsResponse : [])
      setIsLoadingDocs(false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const toggleDocument = (doc: any) => {
    const docId = getDocId(doc)
    if (!docId || existingIdSet.has(docId)) return
    setSelectedFiles((prev) => {
      const next = { ...prev }
      if (next[docId]) {
        delete next[docId]
      } else {
        next[docId] = doc?.name ?? 'Untitled file'
      }
      return next
    })
  }

  const selectedCount = Object.keys(selectedFiles).length

  const filteredDocuments = useMemo(() => {
    const term = docSearch.trim().toLowerCase()
    if (!term) return documents
    return documents.filter((doc) => (doc?.name ?? '').toLowerCase().includes(term))
  }, [documents, docSearch])

  const [, submitFiles, isSubmitting] = useActionState(async () => {
    const selected: SelectedDataboxFile[] = Object.entries(selectedFiles).map(([id, name]) => ({
      id,
      name,
      databoxId: databox!.id,
      databoxName: databox!.name
    }))
    await onConfirm(selected)
    toggle()
    return null
  }, null)

  const onAddClick = () => {
    if (!selectedCount || isSubmitting || !databox) return
    // Called directly from onClick (not a form action), so isPending needs an explicit transition to update.
    startTransition(() => {
      submitFiles()
    })
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg" data-testid="add-databox-files-modal">
      <ModalHeader
        className={`!justify-between !border-0 !flex !items-center chat-primary theme-bg`}
        toggle={toggle}
        close={
          <IconX
            size={24}
            onClick={toggle}
            className="theme-text-subheading transition-colors duration-300 ease-in-out hover:text-white cursor-pointer"
            data-testid="close-button"
          />
        }>
        <div className="flex items-center gap-1">
          <IconPackage size={20} color="white" />
          <span className="text-white">Add files</span>
        </div>
      </ModalHeader>
      <ModalBody className="flex-grow-1">
        {/* Search */}
        <div className="mb-2">
          <InputGroup
            addon={<IconSearch size={14} />}
            addonPosition="start"
            addonType="merged"
            isDarkMode={isDarkMode}
            className="search-input theme-input-group w-full"
            placeholder="Search files"
            value={docSearch}
            onChange={(e: any) => setDocSearch(e.target.value)}
            data-testid="databox-modal-search"
          />
        </div>

        {/* List */}
        <div
          className="flex flex-col min-h-[260px] max-h-[360px] overflow-y-auto rounded bg-[var(--gray-50)]"
          data-testid="databox-modal-list">
          {isLoadingDatabox || isLoadingDocs ? (
            <div className="flex items-center justify-center gap-2 py-8 theme-text-subheading text-body-default">
              <Spinner color="primary" />
              <span>Loading files...</span>
            </div>
          ) : !databox ? (
            <div className="flex items-center justify-center py-8 theme-text-subheading text-body-default">
              No personal databox found.
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="flex items-center justify-center py-8 theme-text-subheading text-body-default">
              No files found.
            </div>
          ) : (
            filteredDocuments.map((doc, index) => {
              const docId = getDocId(doc)
              const alreadyAdded = existingIdSet.has(docId)
              const isChecked = !!selectedFiles[docId]
              return (
                <label
                  key={docId || `doc-${index}`}
                  className={`flex items-center gap-1 p-1 border-b theme-frame-divider last:border-b-0 ${
                    alreadyAdded
                      ? 'opacity-60 cursor-not-allowed'
                      : 'cursor-pointer theme-hover-surface'
                  }`}
                  data-testid="databox-modal-file">
                  <Checkbox
                    color="primary"
                    variant="custom"
                    checked={isChecked || alreadyAdded}
                    disabled={alreadyAdded}
                    onChange={() => toggleDocument(doc)}
                  />
                  <span className="flex-shrink-0">{getFileIcon(doc?.name ?? '')}</span>
                  <span
                    className="flex-1 text-body-default theme-text-heading truncate"
                    title={doc?.name}>
                    {doc?.name ?? 'Untitled file'}
                  </span>
                  {alreadyAdded && (
                    <span className="caption-text theme-text-subheading flex-shrink-0">Added</span>
                  )}
                </label>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between gap-2 mt-3 chat-primary`}>
          <span
            className="text-body-default theme-text-subheading"
            data-testid="databox-modal-count">
            {selectedCount > 0
              ? `${selectedCount} file${selectedCount === 1 ? '' : 's'} selected`
              : 'No files selected'}
          </span>
          <div className="flex items-center gap-1">
            <Button
              label="Cancel"
              variant="outline"
              color="neutral"
              className="dsm-btn-neutral"
              onClick={toggle}
              data-testid="databox-modal-cancel"
            />
            <Button
              label={isSubmitting ? 'Adding...' : 'Add to project'}
              color="primary"
              disabled={selectedCount === 0 || isSubmitting}
              onClick={onAddClick}
              data-testid="databox-modal-confirm"
            />
          </div>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default AddDataboxFilesModal
